import { useCallback, useEffect, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from 'react-query';

import assetProcessingActivitiesApi from 'config/api/assetProcessingActivities/assetProcessingActivities';
import assetRopaApi from 'config/api/assetRopa/assetRopa';
import type { AssetProcessingActivityDetail } from 'config/api/assetRopa/assetRopa.types';
import invalidateOwnerAssetRopaQueries from 'config/api/assetRopa/assetRopaQuery.helpers';
import MUTATION_KEYS from 'config/api/MUTATION_KEYS';
import QUERY_KEYS from 'config/api/QUERY_KEYS';
import useUnsavedChangesProtection from 'hooks/useUnsavedChangesProtection/useUnsavedChangesProtection';
import type { AssetProcessingApaFormValues } from 'pages/ProcessingActivities/_shared/assetProcessingApa.types';

import {
  buildCountryHandlingDiff,
  expandCountryRowsToHandlings,
  isAssetProcessingActivityRevisionConflict,
  isAssetProcessingActivityRevisionValidationError,
  mapDetailToFormValues,
  mapPurposeApplicationToUpdateInput,
} from './assetProcessingActivityEditor.helpers';

type UseAssetProcessingActivityEditorOptions = {
  apaId: number;
  assetRopaId: number;
  initialEditMode?: boolean;
  onConflict?: () => void;
  onNavigate: () => void;
  onRevisionIntegrationError?: () => void;
  onSaveSuccess?: (detail: AssetProcessingActivityDetail, closeAfterSave: boolean) => void;
  onSubmitSuccess?: (detail: AssetProcessingActivityDetail) => void;
  organizationId?: number;
};

const persistApaNestedFields = async (
  organizationId: number,
  apaId: number,
  formValues: AssetProcessingApaFormValues,
  existingDetail: AssetProcessingActivityDetail,
  startingRevision: number,
): Promise<number> => {
  let revision = startingRevision;

  const resolveRevision = async (nextRevision: number | null) => {
    if (nextRevision != null) {
      return nextRevision;
    }

    const { data } = await assetRopaApi.getProcessingActivity(organizationId, apaId);
    return data.revision;
  };

  // Sequential so each nested write can send the latest APA revision.
  // eslint-disable-next-line no-restricted-syntax
  for (const purpose of formValues.purposeApplications) {
    // eslint-disable-next-line no-await-in-loop
    const purposeResult = await assetProcessingActivitiesApi.updatePurpose(
      organizationId,
      apaId,
      purpose.id,
      mapPurposeApplicationToUpdateInput(purpose, revision),
    );

    // eslint-disable-next-line no-await-in-loop
    revision = await resolveRevision(purposeResult.revision);
  }

  const desiredCountries = expandCountryRowsToHandlings(formValues.countryRows);
  const { toCreate, toDeleteIds } = buildCountryHandlingDiff(existingDetail.countries, desiredCountries);

  // eslint-disable-next-line no-restricted-syntax
  for (const countryId of toDeleteIds) {
    // eslint-disable-next-line no-await-in-loop
    const deleteResult = await assetProcessingActivitiesApi.deleteCountry(organizationId, apaId, countryId, { revision });
    // eslint-disable-next-line no-await-in-loop
    revision = await resolveRevision(deleteResult.revision);
  }

  // eslint-disable-next-line no-restricted-syntax
  for (const payload of toCreate) {
    // eslint-disable-next-line no-await-in-loop
    const createResult = await assetProcessingActivitiesApi.createCountry(organizationId, apaId, { ...payload, revision });
    // eslint-disable-next-line no-await-in-loop
    revision = await resolveRevision(createResult.revision);
  }

  return revision;
};

const useAssetProcessingActivityEditor = ({
  apaId,
  initialEditMode = false,
  onConflict,
  onNavigate,
  onRevisionIntegrationError,
  onSaveSuccess,
  onSubmitSuccess,
  organizationId,
}: UseAssetProcessingActivityEditorOptions) => {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(initialEditMode);
  const [formValues, setFormValues] = useState<AssetProcessingApaFormValues | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Partial<Record<string, string>>>({});

  const detailQuery = useQuery(
    [QUERY_KEYS.ASSET_ROPA_APA_DETAIL, organizationId, apaId],
    async () => {
      const { data } = await assetRopaApi.getProcessingActivity(organizationId as number, apaId);
      return data;
    },
    {
      enabled: Boolean(organizationId),
      retry: false,
    },
  );

  useEffect(() => {
    if (detailQuery.data) {
      setFormValues(mapDetailToFormValues(detailQuery.data));
      setIsDirty(false);
      setValidationErrors({});
    }
  }, [detailQuery.data]);

  const handleFormChange = useCallback((nextValues: AssetProcessingApaFormValues) => {
    setFormValues(nextValues);
    setIsDirty(true);
    setValidationErrors({});
  }, []);

  const handleMutationError = useCallback(
    async (error: unknown) => {
      if (isAssetProcessingActivityRevisionConflict(error)) {
        await detailQuery.refetch();
        onConflict?.();
        return;
      }

      if (isAssetProcessingActivityRevisionValidationError(error)) {
        onRevisionIntegrationError?.();
      }
    },
    [detailQuery, onConflict, onRevisionIntegrationError],
  );

  const saveMutation = useMutation(
    MUTATION_KEYS.ASSET_ROPA_APA_UPDATE,
    async (closeAfterSave: boolean) => {
      if (!formValues || !detailQuery.data || !organizationId) return null;

      const { data: updatedDetail } = await assetRopaApi.updateProcessingActivity(organizationId, apaId, {
        dpiaIndicationStatus: formValues.dpiaIndicationStatus as AssetProcessingActivityDetail['dpiaIndicationStatus'],
        dpiaIndicationComment: formValues.dpiaIndicationComment,
        minimumSecurityAssessment: formValues.minimumSecurityAssessment as AssetProcessingActivityDetail['minimumSecurityAssessment'],
        minimumSecurityAssessmentComment: formValues.minimumSecurityAssessmentComment,
        updatedAt: detailQuery.data.updatedAt,
        revision: detailQuery.data.revision,
      });

      await persistApaNestedFields(organizationId, apaId, formValues, detailQuery.data, updatedDetail.revision);

      const { data } = await assetRopaApi.getProcessingActivity(organizationId, apaId);
      return { closeAfterSave, data };
    },
    {
      onSuccess: result => {
        if (!result) return;

        queryClient.setQueryData([QUERY_KEYS.ASSET_ROPA_APA_DETAIL, organizationId, apaId], result.data);
        Promise.resolve(invalidateOwnerAssetRopaQueries(queryClient, organizationId)).catch(() => undefined);
        setFormValues(mapDetailToFormValues(result.data));
        setIsDirty(false);
        onSaveSuccess?.(result.data, result.closeAfterSave);
      },
      onError: (error: unknown) => {
        handleMutationError(error).catch(() => undefined);
      },
    },
  );

  const submitMutation = useMutation(
    MUTATION_KEYS.ASSET_ROPA_APA_SUBMIT,
    async (revision: number) => {
      if (!detailQuery.data || !organizationId) return null;

      const { data } = await assetRopaApi.submitProcessingActivity(organizationId, apaId, {
        updatedAt: detailQuery.data.updatedAt,
        revision,
      });

      return data;
    },
    {
      onSuccess: data => {
        if (!data) return;

        queryClient.setQueryData([QUERY_KEYS.ASSET_ROPA_APA_DETAIL, organizationId, apaId], data);
        Promise.resolve(invalidateOwnerAssetRopaQueries(queryClient, organizationId)).catch(() => undefined);
        setIsDirty(false);
        onSubmitSuccess?.(data);
      },
      onError: (error: unknown) => {
        handleMutationError(error).catch(() => undefined);
      },
    },
  );

  const handleSubmit = useCallback(async () => {
    let revision = detailQuery.data?.revision;
    if (revision == null) return;

    if (isDirty) {
      const saved = await saveMutation.mutateAsync(false);
      revision = saved?.data.revision ?? revision;
    }

    await submitMutation.mutateAsync(revision);
  }, [detailQuery.data?.revision, isDirty, saveMutation, submitMutation]);

  const { NavigationPrompt, handleNavigation } = useUnsavedChangesProtection({
    enabled: isEditing,
    isDirty,
    onNavigate,
    onSave: async () => {
      await saveMutation.mutateAsync(false);
    },
  });

  const readOnly = !isEditing || detailQuery.data?.review?.status === 'submitted' || detailQuery.data?.review?.status === 'accepted';

  return {
    detail: detailQuery.data,
    formValues,
    handleFormChange,
    handleNavigation,
    handleSubmit,
    isDirty,
    isEditing,
    isError: detailQuery.isError,
    isLoading: detailQuery.isLoading,
    NavigationPrompt,
    readOnly,
    refetch: detailQuery.refetch,
    saveMutation,
    setIsEditing,
    setValidationErrors,
    submitMutation,
    validationErrors,
  };
};

export default useAssetProcessingActivityEditor;
